import { Loading } from '@components/atomic/atoms';
import { ConfirmModal, RejectModal, SuccessModal } from '@components/atomic/organisms';
import { ArrowRight, ConfirmSolid, ExclamationBrown, IcoBook, IcoConfirmCheck, IcoExtendSampling, IcoUnion, RejectSolid } from '@icons';
import { closeSampling, extendSampling, getSamplingHistory, getSamplingStatistics } from '@lib/api';
import { formatToDecimals } from '@lib/helpers/formatting';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Button, Col, ProgressBar, Row, Spinner } from 'react-bootstrap';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import SamplingInfoBoxDropdown from './SamplingInfoBoxDropdown';
import SamplingRulesModal from './SamplingRulesModal';

interface SamplingInfoBoxProps {
	invalidateQuery: () => void;
	resetQueryParams: () => void;
	project: {
		name: string;
		samplingRuleAssignment?: {
			samplingRule: {
				name: string;
				description: string;
				code: string;
			};
		};
	};
}

const SamplingInfoBox: React.FC<SamplingInfoBoxProps> = ({ invalidateQuery, resetQueryParams, project }) => {
	const pathname = useLocation().pathname;
	const completeListParam = `complete-list=true`;
	const projectId = useParams<{ projectId: string }>().projectId;
	const history = useHistory();

	const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

	const { data: samplingHistory, isLoading: isLoadingSamplingHistory } = useQuery({
		queryKey: ['samplingHistory', projectId],
		queryFn: async () => {
			const result = await getSamplingHistory(projectId, 0);

			if (!result.success) {
				throw new Error('Failed to fetch sampling history');
			}

			const samplingList = result.data;
			const id = samplingList.find((el) => !el.completed)?.id ?? null;

			return { samplingList, id };
		},
	});

	// console.log('samplingHistory', samplingHistory);
	const sampleId = samplingHistory?.id;
	const queryClient = useQueryClient();

	const {
		data: statistics,
		isLoading: isLoadingStatistics,
		// 		isError: isErrorSamplingStatistics,
	} = useQuery({
		queryKey: ['samplingStatistics', projectId, sampleId],
		queryFn: async () => {
			const result = await getSamplingStatistics(projectId, sampleId);
			if (!result.success) {
				throw new Error('Failed to fetch sampling statistics');
			}
			return result.data;
		},
		enabled: !isLoadingSamplingHistory,
	});

	const closeSamplingMutation = useMutation({
		mutationFn: async () => {
			const response = await closeSampling(projectId, sampleId);

			if (response.success) {
				return response;
			}
			throw new Error(response.message || 'Error closing sampling');
		},
		onSuccess: () => {
			// setExternalSupplies(result.data);
			invalidateQuery();
			queryClient.invalidateQueries({ queryKey: ['samplingHistory', projectId] });
			queryClient.invalidateQueries({ queryKey: ['infoSampling', projectId] });
			if (sampleId) {
				queryClient.invalidateQueries({ queryKey: ['samplingStatistics', projectId, sampleId] });
			}
		},
		onError: (error) => {
			console.error('Error closing sampling:', error);
		},
	});

	const extendSamplingMutation = useMutation({
		mutationFn: async () => {
			const response = await extendSampling(projectId, sampleId);

			if (response.success) {
				return response;
			}
			throw new Error(response.message || 'Error extending sampling');
		},
		onSuccess: (result) => {
			// setExternalSupplies(result.data);
			invalidateQuery();
			queryClient.invalidateQueries({ queryKey: ['samplingHistory', projectId] });
			queryClient.invalidateQueries({ queryKey: ['infoSampling', projectId] });
			if (sampleId) {
				queryClient.invalidateQueries({ queryKey: ['samplingStatistics', projectId, sampleId] });
			}
		},
		onError: (error) => {
			console.error('Error extending sampling:', error);
		},
	});

	const dropdownItems = [
		{
			key: 'rules',
			label: <span>Consulta le regole del bando</span>,
			action: () => {
				setIsRuleModalOpen(true);
			},
			icon: <IcoBook fill="#1C2743" />,
		},
		{
			key: 'extend',
			label: <span>Estendi il campionamento</span>,
			action: () => {
				setIsConfirmModalOpen(true);
			},
			icon: <IcoExtendSampling fill="#1C2743" width={24} height={24} />,
		},
		{
			label: (
				<span
					style={{
						color: '#CC334C',
					}}
				>
					Concludi la fase di controllo
				</span>
			),
			key: 'conclude',
			action: closeSamplingMutation.mutate,
			icon: null,
		},
	];

	return (
		<>
			<div
				style={{
					backgroundColor: '#E3E7EB',
					padding: '1.5rem 3rem',
					height: '100%',
					borderRadius: '12px',
				}}
			>
				<Row>
					<Col className="col-6">
						<Row>
							<Col className="col-auto align-self-center">
								<IcoUnion />
							</Col>
							<Col>
								<Row className="align-items-center">
									<Col className="col-auto">
										<span className="body-text-L-bold mb-1">Lista campionata</span>
									</Col>
									<Col className="col-auto pb-1 px-2">
										<Button
											onClick={() => {
												history.push(`${pathname}?${completeListParam}`);
												resetQueryParams();
											}}
											className="btn custom-ghost-button ms-auto no-underline"
										>
											<span style={{ color: '#1C2743', textTransform: 'none', fontWeight: '500', verticalAlign: 'middle' }}>
												vedi la lista completa
											</span>
											<ArrowRight className="ms-2 ico-path-fill-primary" />
										</Button>
									</Col>
								</Row>

								<Row className="mt-3">
									<Col className="f-400">Sottoinsieme basato sui criteri di campionamento configurati.</Col>
								</Row>
							</Col>
						</Row>
					</Col>

					<Col className="col-6 align-self-center">
						<Row>
							<Col
								style={{
									display: 'flex',
									justifyContent: 'flex-end',
								}}
							>
								<SamplingInfoBoxDropdown dropdownItems={dropdownItems} />
							</Col>
							{/* <Col className="col-auto">
								<Button
									variant="link"
									className="btn ms-auto"
									style={{ textDecorationColor: '#1C2743' }}
									// onClick={() => setShowModalRuleSampling(true)}
								>
									<span style={{ color: '#1C2743', textTransform: 'none', fontWeight: '500' }}>consulta le regole del bando</span>
								</Button>
							</Col>
							<Col className="col-auto">
								<Button className="btn new custom-outline-button d-flex align-items-center" onClick={() => closeSamplingMutation.mutate()}>
									Concludi la fase di controllo
									{closeSamplingMutation.isPending && (
										<Spinner
											as="span"
											className="ms-2 ico-path-fill-white"
											animation="border"
											size="sm"
											role="status"
											aria-hidden="true"
											style={{ verticalAlign: 'revert-layer' }}
										/>
									)}
								</Button>
							</Col> */}
						</Row>
					</Col>
				</Row>

				<hr style={{ borderColor: '#2A3A64', opacity: 1 }} />

				{isLoadingStatistics ? (
					<Row className="justify-content-center">
						<Col>
							<Loading fixedHeight="25vh" />
						</Col>
					</Row>
				) : (
					<>
						<Row className="mt-4">
							<Col className="col-7" style={{ borderRight: '1px solid #2A3A64' }}>
								<Row className="justify-content-start">
									<Col className="col-9">
										<span className="f-700">
											{`${statistics.checkCompleted + statistics.checkCompletedWithErrors}/${statistics.totalInvoices ?? 0} catene documentali
								controllate`}
										</span>
									</Col>
									<Col className="col-2 text-end">
										<span className="f-700">
											{formatToDecimals(
												((statistics.checkCompletedWithErrors + statistics.checkCompleted) / statistics.totalInvoices) * 100,
												0,
											)}
											%
										</span>
									</Col>
								</Row>

								<Row className="mt-2">
									<Col className="col-11">
										<ProgressBar>
											<ProgressBar
												variant="danger"
												className="new"
												now={statistics.percentageCompletedWithErrors ?? 0}
												key={'red' + statistics.percentageCompletedWithErrors} //per forzare il rerender quando cambiano i valori
												label={`${formatToDecimals(statistics.percentageCompletedWithErrors, 0)}%`}
												visuallyHidden
												striped
											/>
											<ProgressBar
												variant="success"
												className="new"
												now={statistics.percentageCompleted ?? 0}
												key={'green' + statistics.percentageCompleted} //per forzare il rerender quando cambiano i valori
												label={`${formatToDecimals(statistics.percentageCompleted, 0)}%`}
												visuallyHidden
											/>
										</ProgressBar>
									</Col>
								</Row>

								<Row className="mt-3 align-items-center">
									<Col className="col-auto d-flex align-items-center pt-1 pe-1">
										<i className="icon-square bg-green"></i>
									</Col>
									<Col className="col-auto">
										<ConfirmSolid width={20} height={20} />
									</Col>
									<Col className="ps-0 pt-1">
										<span className="f-700">
											{statistics.checkCompleted ?? 0} catene documentali conformi ({formatToDecimals(statistics.percentageCompleted, 0)}%)
										</span>
										<span className="f-400 ps-2">{formatToDecimals(statistics.completedAmount)}€ eleggibili su importo campionato</span>
									</Col>
								</Row>

								<Row className="mt-3 align-items-center">
									<Col className="col-auto d-flex align-items-center pt-1 pe-1">
										<i className="icon-square bg-red-stripes"></i>
									</Col>
									<Col className="col-auto">
										<RejectSolid width={20} height={20} />
									</Col>
									<Col className="ps-0 pt-1">
										<span className="f-700">
											{statistics.checkCompletedWithErrors ?? 0} catene documentali non conformi (
											{formatToDecimals(statistics.percentageCompletedWithErrors, 0)}%)
										</span>
										<span className="f-400 ps-2">{formatToDecimals(statistics.errorAmount)}€ non eleggibili su importo complessivo</span>
									</Col>
								</Row>
							</Col>

							<Col className="col-5 align-content-end px-3">
								<Row className="justify-content-between">
									<Col>
										<span className="f-700">
											{statistics.checkAssignedCompleted ?? 0}/{statistics.totalAssignedInvoices ?? 0} catene documentali controllate
											assegnate a me
										</span>
									</Col>
									<Col className="col-auto">
										<span className="f-700">{formatToDecimals(statistics.percentageAssignedCompleted, 0)}%</span>
									</Col>
								</Row>

								<Row className="mt-2">
									<Col>
										<ProgressBar
											className="new blue"
											now={statistics.percentageAssignedCompleted ?? 0}
											label={`${formatToDecimals(statistics.percentageAssignedCompleted, 0)}%`}
											visuallyHidden
										/>
									</Col>
								</Row>
							</Col>
						</Row>

						{statistics.percentageAmountError >= 2 ? (
							<Row className="mt-5 mb-3 new-alert-banner mx-0">
								<Col>
									<Row>
										<Col className="col-auto pe-2">
											<ExclamationBrown fill="#995C00" width={20} height={20} />
										</Col>
										<Col className="ps-0">
											<span className="f-700 text-uppercase d-block" style={{ color: '#995C00' }}>
												ATTENZIONE:
											</span>
										</Col>
									</Row>
									<Row className="mt-2">
										<span className="f-700 d-block">
											Hai superato la percentuale di catene documentali non conformi:{' '}
											{formatToDecimals(statistics.percentageAmountError, 0)}%
										</span>
									</Row>
									<Row>
										<span className="f-400">
											(percentuale massima accettabile: {formatToDecimals(statistics.maxErrorsAmountPercentage, 0)}%)
										</span>
									</Row>
								</Col>

								<Col className="col-auto align-self-center px-0">
									<Button className="btn new custom-primary-button d-flex align-items-center" onClick={() => setIsConfirmModalOpen(true)}>
										ESTENDI CAMPIONAMENTO
										{extendSamplingMutation.isPending && (
											<Spinner
												as="span"
												className="ms-2 ico-path-fill-white"
												animation="border"
												size="sm"
												role="status"
												aria-hidden="true"
												style={{ verticalAlign: 'revert-layer' }}
											/>
										)}
									</Button>
									<ConfirmModal
										show={isConfirmModalOpen}
										callback={extendSamplingMutation.mutate}
										onHide={() => {
											setIsConfirmModalOpen(false);
										}}
										title="Sei sicuro di voler estendere il campionamento?"
										message="Aggiungerai catene documentali da sottoporre a controllo."
										confirmButtonLabel={
											<span
												style={{
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													gap: '0.5rem',
												}}
											>
												<IcoConfirmCheck fill="white" />
												Sì, conferma
											</span>
										}
									/>

									{/* <Button
										className="btn new custom-primary-button d-flex align-items-center"
										onClick={() => extendSamplingMutation.mutate()}
									>
										Aggiungi catene documentali al campionamento
										{extendSamplingMutation.isPending && (
											<Spinner
												as="span"
												className="ms-2 ico-path-fill-white"
												animation="border"
												size="sm"
												role="status"
												aria-hidden="true"
												style={{ verticalAlign: 'revert-layer' }}
											/>
										)}
									</Button> */}
								</Col>
							</Row>
						) : null}
					</>
				)}
			</div>

			<SamplingRulesModal
				show={isRuleModalOpen}
				onHide={() => setIsRuleModalOpen(false)}
				name={project?.samplingRuleAssignment?.samplingRule?.name}
				description={project?.samplingRuleAssignment?.samplingRule?.description}
			/>

			<SuccessModal
				show={extendSamplingMutation.isSuccess}
				onHide={extendSamplingMutation.reset}
				message="Estensione del campione eseguita con successo!"
			/>

			<RejectModal
				show={extendSamplingMutation.isError}
				onHide={extendSamplingMutation.reset}
				message={extendSamplingMutation?.error?.message || "Si è verificato un errore durante l'operazione."}
				tryAgain={extendSamplingMutation.mutate}
			/>

			<SuccessModal
				show={closeSamplingMutation.isSuccess}
				onHide={closeSamplingMutation.reset}
				message="Chiusura del campionamento eseguita con successo!"
			/>

			<RejectModal
				show={closeSamplingMutation.isError}
				onHide={closeSamplingMutation.reset}
				message={closeSamplingMutation?.error?.message || "Si è verificato un errore durante l'operazione."}
				tryAgain={closeSamplingMutation.mutate}
			/>
		</>
	);
};

export default SamplingInfoBox;
